import { ErrorResponseSchema } from '@kioskforge/shared';
import { pb } from './pocketbase';

export class ApiError extends Error {
  readonly code: string;
  readonly requestId: string;
  readonly status: number;
  readonly details: unknown;

  constructor(message: string, code: string, requestId: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.requestId = requestId;
    this.status = status;
    this.details = details;
  }
}

export type RequestOptions = {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** Caller headers. Merged FIRST — can never clobber the auth header. */
  headers?: Record<string, string>;
};

/** Friendly message for an expired session (never raw server text). */
export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Sign in again.';

export class SessionExpiredError extends ApiError {
  constructor() {
    super(SESSION_EXPIRED_MESSAGE, 'session_expired', 'unknown', 401);
    this.name = 'SessionExpiredError';
  }
}

/** Call-time token read: the auth store may be restored or refreshed later. */
export function bearerValue(): string {
  const store = pb.authStore;
  return store.isValid ? String(store.token ?? '') : '';
}

async function parseError(response: Response): Promise<ApiError> {
  const payload: unknown = await response.json().catch(() => null);
  const parsed = ErrorResponseSchema.safeParse(payload);
  if (parsed.success) {
    const { code, message, request_id, details } = parsed.data.error;
    return new ApiError(message, code, request_id, response.status, details);
  }
  return new ApiError(response.statusText, 'unexpected_error', 'unknown', response.status);
}

function sessionExpired(): SessionExpiredError {
  pb.authStore.clear();
  return new SessionExpiredError();
}

async function readOk<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = (): Promise<Response> => {
    const token = bearerValue();
    return fetch(`/api/v1${path}`, {
      method: options.method ?? 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers
      },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal
    });
  };

  const first = await send();
  if (first.status === 401) {
    // One refresh, one retry (review §Bug 1). Still 401 → sign out, friendly error.
    if (pb.authStore.isValid) {
      try {
        await pb.collection('users').authRefresh();
      } catch {
        throw sessionExpired();
      }
      const second = await send();
      if (second.ok) return readOk<T>(second);
      if (second.status === 401) throw sessionExpired();
      throw await parseError(second);
    }
    throw sessionExpired();
  }
  if (first.ok) return readOk<T>(first);
  throw await parseError(first);
}
